import { memo, useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import _isEmpty from 'lodash/isEmpty';
//Local files
import { CardItem } from './FirstFlows.styled';
import FirstAnalysesList from 'components/Analyses/FirstAnalyses/List/List';
import PhoneField from 'components/Common/PhoneField/PhoneField';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useFirstFlows from 'hooks/useFirstFlows';
import useFirstAnalyses from 'hooks/useFirstAnalyses';
import { DELETE_FIRST_FLOW } from 'helpers/confirmations';
import { checkEmptyString, needToUpdateField } from 'helpers';
import { firstFlowsSelectors } from "features/firstFlows";

const FirstFlows = () => {
  const { openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getFirstFlow, createFirstFlow, updateFirstFlow } = useFirstFlows();
  const { getFirstAnalyses, clearLocalFirstAnalyses } = useFirstAnalyses();
  const id = useCustomSelector(firstFlowsSelectors.getFirstFlowId);
  const firstFlow = useCustomSelector(firstFlowsSelectors.getFirstFlowData);
  const fetching = useCustomSelector(firstFlowsSelectors.firstFlowIsFetching);
  const [phone, setPhone] = useState({ value: '', formattedValue: '', error: '' });
  const [prompt, setPrompt] = useState({ value: '', error: '' });
  const [edit, setEdit] = useState(false);
  const [copied, setCopied] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const handleEntering = (first_flow) => {
    setPhone({ value: first_flow?.phone, formattedValue: first_flow?.phone, error: '' });
    setPrompt({ value: first_flow?.prompt, error: '' });
  };
  const handleExited = () => {
    setPhone({ value: '', formattedValue: '', error: '' });
    setPrompt({ value: '', error: '' });
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;

    if (!checkEmptyString(phone.value)) {
      setPhone(prev => ({ ...prev, error: `Phone can't be empty` }) );
      hasError = true;
    }
    if (!checkEmptyString(prompt.value)) {
      setPrompt(prev => ({ ...prev, error: `Prompt can't be empty` }) );
      hasError = true;
    }
    if (phone.error) hasError = true;
    if (hasError) return;

    const first_flow = {
      ...(needToUpdateField(firstFlow?.phone, phone.value) && {  phone: phone.value }),
      ...(needToUpdateField(firstFlow?.prompt, prompt.value) && { prompt: prompt.value }),
    };

    if (!_isEmpty(first_flow)) {
      if (!!id) {
        updateFirstFlow(first_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createFirstFlow(first_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_FIRST_FLOW });
  const handleCopy = () => {
    if (!!firstFlow?.email && !copied)
    {
      navigator.clipboard.writeText(firstFlow.email);
      setCopied(true);
    }
  };
  const fetchFirstAnalyses = useCallback(() => {
    getFirstAnalyses({ offset: 0 });
  }, [getFirstAnalyses]);
  
  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalFirstAnalyses();
    };
  }, [id, clearLocalFirstAnalyses]);
  useEffect(() => {
    if (firstTimeOpened) {
      getFirstFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { first_flow } = payload;
          handleEntering(first_flow);
          fetchFirstAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
  }, [getFirstFlow, fetchFirstAnalyses, firstTimeOpened]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <Tooltip 
              title={!!firstFlow?.email ? (copied ? 'Copied!' : 'Click to copy') : ''}
              followCursor
              onClose={() => setTimeout(() => setCopied(false), 300)} 
            >
              <Typography 
                variant='subtitle2'
                component='div' 
                sx={{ wordBreak: 'break-all', cursor: 'pointer' }}
                onClick={handleCopy}
              >
                {`Receiving Email: ${!!firstFlow?.email ? firstFlow.email : '-'}`}
              </Typography>
            </Tooltip>
            <PhoneField
              id='phone'
              sx={{ mt: '16px', mb: '8px' }}
              fullWidth
              label='Receiving Phone Number'
              formattedValue={phone.formattedValue}
              disabled={fetching || (!!id && !edit)}
              error={!!phone.error}
              helperText={phone.error}
              onChange={({ value, formattedValue, error }) => setPhone({ value, formattedValue, error })}
            />
            <TextField
              fullWidth
              label='Prompt'
              multiline
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={prompt.error}
              error={!!prompt.error}
              value={prompt.value}
              onChange={({ target: { value } }) => setPrompt({ value, error: '' })}
            />
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <FirstAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <FirstFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);